/**
 * 7-Scents Luxury Cart System
 * Shared modules for Shopify Ajax cart state, section refreshes,
 * reward/shipping calculations, and premium micro-animations.
 */
(function () {
  if (window.CartManager && window.RewardManager && window.ShippingManager && window.LuxuryAnimations) return;

  const ROOT_URL = window.Shopify?.routes?.root || '/';
  const CART_URL = window.routes?.cart_url || `${ROOT_URL}cart`;
  const CART_ADD_URL = window.routes?.cart_add_url || `${ROOT_URL}cart/add.js`;
  const CART_CHANGE_URL = window.routes?.cart_change_url || `${ROOT_URL}cart/change.js`;
  const CART_UPDATE_URL = window.routes?.cart_update_url || `${ROOT_URL}cart/update.js`;
  const CART_JSON_URL = `${CART_URL}.js`.replace('/cart.js.js', '/cart.js');
  const CART_CLEAR_URL = `${ROOT_URL}cart/clear.js`;

  class EventBus {
    constructor() {
      this.listeners = new Map();
    }

    on(event, handler) {
      if (!this.listeners.has(event)) this.listeners.set(event, new Set());
      this.listeners.get(event).add(handler);
      return () => this.listeners.get(event)?.delete(handler);
    }

    emit(event, payload) {
      this.listeners.get(event)?.forEach((handler) => {
        try {
          handler(payload);
        } catch (error) {
          console.error(`[LuxuryCartSystem] ${event} listener failed`, error);
        }
      });
    }
  }

  const LuxuryCartUtils = {
    clamp(value, min = 0, max = 100) {
      return Math.max(min, Math.min(max, value));
    },

    debounce(fn, wait = 120) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(null, args), wait);
      };
    },

    nextFrame(callback) {
      return requestAnimationFrame(() => requestAnimationFrame(callback));
    },

    formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || 'INR';
      const amount = (Number(cents) || 0) / 100;

      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme?.moneyFormat) {
        try {
          return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
        } catch (error) {
          console.warn('[LuxuryCartSystem] Shopify.formatMoney failed, using Intl fallback', error);
        }
      }

      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency,
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    },

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return parsed.querySelector(selector)?.innerHTML || '';
    },

    replaceSectionHTML({ rootId, html, selector = '.js-contents' }) {
      if (!html) return;
      const root = document.getElementById(rootId);
      if (!root) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector(selector);
      const target = root.querySelector(selector) || root;
      if (source && target) target.innerHTML = source.innerHTML;
    },

    unique(values) {
      return Array.from(new Set(values.filter(Boolean)));
    },
  };

  const LuxuryAnimations = {
    animateNumber({ from, to, duration = 520, onUpdate, onComplete }) {
      const start = performance.now();
      const startValue = Number.isFinite(from) ? from : to;
      const change = to - startValue;
      const ease = (t) => 1 - Math.pow(1 - t, 3);

      const tick = (now) => {
        const elapsed = Math.min(1, (now - start) / duration);
        onUpdate(startValue + change * ease(elapsed));

        if (elapsed < 1) {
          requestAnimationFrame(tick);
        } else {
          onUpdate(to);
          onComplete?.();
        }
      };

      requestAnimationFrame(tick);
    },

    animateMoneyElement(element, fromCents, toCents) {
      if (!element || fromCents === toCents) return;

      element.classList.add('lux-money-is-animating');
      this.animateNumber({
        from: fromCents,
        to: toCents,
        onUpdate: (value) => {
          element.textContent = LuxuryCartUtils.formatMoney(Math.round(value));
        },
        onComplete: () => {
          element.dataset.cartMoneyValue = String(toCents);
          element.classList.remove('lux-money-is-animating');
          element.classList.add('lux-money-just-updated');
          setTimeout(() => element.classList.remove('lux-money-just-updated'), 380);
        },
      });
    },

    animateCartNumbers(previousCart, cart) {
      if (!previousCart || !cart) return;

      const values = {
        total: [previousCart.total_price, cart.total_price],
        subtotal: [previousCart.items_subtotal_price, cart.items_subtotal_price],
      };

      document.querySelectorAll('[data-cart-money]').forEach((element) => {
        const key = element.dataset.cartMoney;
        const pair = values[key];
        const next = Number(pair?.[1]);
        const previous = Number(pair?.[0]);
        if (!Number.isFinite(previous) || !Number.isFinite(next)) return;
        this.animateMoneyElement(element, previous, next);
      });
    },

    pulse(element, className = 'lux-pulse') {
      if (!element) return;
      element.classList.remove(className);
      void element.offsetWidth;
      element.classList.add(className);
    },

    confetti(container) {
      if (!container || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const cloud = document.createElement('span');
      cloud.className = 'lux-confetti';
      cloud.setAttribute('aria-hidden', 'true');

      for (let i = 0; i < 16; i += 1) {
        const piece = document.createElement('span');
        piece.className = 'lux-confetti__piece';
        piece.style.setProperty('--lux-confetti-x', `${Math.round((Math.random() - 0.5) * 120)}px`);
        piece.style.setProperty('--lux-confetti-y', `${Math.round(-24 - Math.random() * 66)}px`);
        piece.style.setProperty('--lux-confetti-r', `${Math.round(Math.random() * 220 - 110)}deg`);
        piece.style.setProperty('--lux-confetti-delay', `${Math.random() * 90}ms`);
        cloud.appendChild(piece);
      }

      container.appendChild(cloud);
      setTimeout(() => cloud.remove(), 1100);
    },
  };

  const ProgressEngine = {
    between(value, start, end) {
      if (end <= start) return 100;
      return LuxuryCartUtils.clamp(((value - start) / (end - start)) * 100);
    },

    toward(value, threshold) {
      if (!threshold) return 100;
      return LuxuryCartUtils.clamp((value / threshold) * 100);
    },
  };

  class CartManagerClass {
    constructor() {
      this.bus = new EventBus();
      this.cart = null;
      this.previousCart = null;
      this._queue = Promise.resolve();
      this._initialCartPromise = null;
      this._debounceTimer = null;
      this._sectionRefreshTimer = null;
      this._pubsubWired = false;
      this._managedFetchDepth = 0;
      this._nativeFetch = window.fetch.bind(window);
      this._wireShopifyPubSub();
      this._wireAjaxCartObserver();
      setTimeout(() => this._wireShopifyPubSub(), 0);
    }

    on(event, handler) {
      return this.bus.on(event, handler);
    }

    getCart() {
      if (this.cart) return Promise.resolve(this.cart);
      if (!this._initialCartPromise) {
        this._initialCartPromise = this.refresh({ animate: false, source: 'initial' }).finally(() => {
          this._initialCartPromise = null;
        });
      }
      return this._initialCartPromise;
    }

    refresh({ animate = true, source = 'refresh', renderSections = false } = {}) {
      return this._enqueue(async () => {
        const cart = await this._fetchCartRaw();
        this.receiveCart(cart, { animate, source });
        if (renderSections) await this.refreshSections();
        return cart;
      });
    }

    scheduleRefresh(delay = 120, options = {}) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this.refresh(options).catch(() => {}), delay);
    }

    scheduleSectionRefresh(delay = 120) {
      clearTimeout(this._sectionRefreshTimer);
      this._sectionRefreshTimer = setTimeout(() => this.refreshSections().catch(() => {}), delay);
    }

    async refreshSections() {
      const sections = this._collectSectionsForRendering();
      if (!sections.length) return null;

      const url = new URL(CART_URL, window.location.origin);
      url.searchParams.set('sections', sections.join(','));
      url.searchParams.set('sections_url', window.location.pathname);

      const response = await this._fetchManaged(url.toString(), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Could not fetch cart sections');

      const renderedSections = await response.json();
      this._renderCartSections(renderedSections);
      return renderedSections;
    }

    receiveCart(cart, { animate = true, source = 'external' } = {}) {
      if (!cart || typeof cart.item_count !== 'number') return cart;

      const previousCart = this.cart;
      this.previousCart = previousCart;
      this.cart = cart;

      this._syncCartDom(cart, previousCart, { animate });
      this.bus.emit('cart:updated', cart);
      document.dispatchEvent(
        new CustomEvent('luxury-cart:updated', {
          detail: { cart, previousCart, source },
        })
      );
      document.dispatchEvent(new CustomEvent('cart:refresh-ui'));
      return cart;
    }

    addItems(items, { openDrawer = true } = {}) {
      const cleanItems = (items || [])
        .filter((item) => item?.id && Number(item.quantity) > 0)
        .map((item) => ({
          ...item,
          id: Number(item.id),
          quantity: Number(item.quantity),
        }));

      if (!cleanItems.length) return Promise.resolve(this.cart);

      return this._enqueue(
        async () => {
          const sections = this._collectSectionsForRendering();
          const response = await this._fetchManaged(CART_ADD_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: cleanItems,
              sections,
              sections_url: window.location.pathname,
            }),
          }).then((res) => res.json());

          if (response.status) {
            throw new Error(response.description || response.message || 'Could not add items to cart');
          }

          this._renderAddResponse(response, { openDrawer });
          const cart = await this._fetchCartRaw();
          this.receiveCart(cart, { source: 'add-items' });
          this.bus.emit('cart:items-added', cleanItems);
          return cart;
        },
        { busy: true }
      );
    }

    changeLine(line, quantity, { renderSections = true } = {}) {
      return this._enqueue(
        async () => {
          const body = { line, quantity };

          if (renderSections) {
            body.sections = this._collectSectionsForRendering();
            body.sections_url = window.location.pathname;
          }

          const cart = await this._fetchManaged(CART_CHANGE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          }).then((res) => res.json());

          if (cart.status || cart.errors) {
            throw new Error(cart.description || cart.message || cart.errors || 'Could not update cart');
          }

          if (cart.sections) this._renderCartSections(cart.sections);
          this.receiveCart(cart, { source: 'change-line' });
          return cart;
        },
        { busy: true }
      );
    }

    updateCart(updates) {
      return this._enqueue(
        async () => {
          const cart = await this._fetchManaged(CART_UPDATE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              updates,
              sections: this._collectSectionsForRendering(),
              sections_url: window.location.pathname,
            }),
          }).then((res) => res.json());

          if (cart.status || cart.errors) {
            throw new Error(cart.description || cart.message || cart.errors || 'Could not update cart');
          }

          if (cart.sections) this._renderCartSections(cart.sections);
          this.receiveCart(cart, { source: 'cart-update' });
          return cart;
        },
        { busy: true }
      );
    }

    getSubtotal() {
      return this.cart?.items_subtotal_price || 0;
    }

    _enqueue(task, { busy = false } = {}) {
      const run = async () => {
        if (busy) this._setBusy(true);
        try {
          return await task();
        } catch (error) {
          console.error('[CartManager] request failed', error);
          this.bus.emit('cart:error', error);
          throw error;
        } finally {
          if (busy) this._setBusy(false);
        }
      };

      this._queue = this._queue.then(run, run);
      return this._queue;
    }

    async _fetchCartRaw() {
      const response = await this._fetchManaged(CART_JSON_URL, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Could not fetch cart');
      return response.json();
    }

    _fetchManaged(...args) {
      this._managedFetchDepth += 1;
      return this._nativeFetch(...args).finally(() => {
        this._managedFetchDepth = Math.max(this._managedFetchDepth - 1, 0);
      });
    }

    _setBusy(isBusy) {
      document.documentElement.classList.toggle('lux-cart-is-loading', isBusy);
      this.bus.emit('cart:loading', isBusy);
    }

    _syncCartDom(cart, previousCart, { animate }) {
      const isEmpty = cart.item_count === 0;
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
      document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);

      document.querySelectorAll('.cart__checkout-button').forEach((button) => {
        button.toggleAttribute('disabled', isEmpty);
      });

      if (animate) LuxuryAnimations.animateCartNumbers(previousCart, cart);
    }

    _collectSectionsForRendering() {
      const sectionIds = [];
      const drawer = document.querySelector('cart-drawer');

      if (drawer?.getSectionsToRender) {
        sectionIds.push(...drawer.getSectionsToRender().map((section) => section.id || section.section));
      } else {
        sectionIds.push('cart-icon-bubble');
      }

      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems?.dataset.id) sectionIds.push(mainCartItems.dataset.id);

      const mainCartFooter = document.getElementById('main-cart-footer');
      if (mainCartFooter?.dataset.id) sectionIds.push(mainCartFooter.dataset.id);

      sectionIds.push('cart-live-region-text');
      return LuxuryCartUtils.unique(sectionIds);
    }

    _renderAddResponse(response, { openDrawer }) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && response.sections && openDrawer) {
        drawer.renderContents(response);
      } else if (response.sections) {
        this._renderCartSections(response.sections);
      }
    }

    _renderCartSections(sections) {
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems?.dataset.id && sections[mainCartItems.dataset.id]) {
        LuxuryCartUtils.replaceSectionHTML({
          rootId: 'main-cart-items',
          html: sections[mainCartItems.dataset.id],
          selector: '.js-contents',
        });
      }

      const mainCartFooter = document.getElementById('main-cart-footer');
      if (mainCartFooter?.dataset.id && sections[mainCartFooter.dataset.id]) {
        LuxuryCartUtils.replaceSectionHTML({
          rootId: 'main-cart-footer',
          html: sections[mainCartFooter.dataset.id],
          selector: '.js-contents',
        });
      }

      if (sections['cart-drawer']) {
        const drawer = document.querySelector('cart-drawer');
        const target = document.getElementById('CartDrawer');
        const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const source = parsed.querySelector('#CartDrawer');

        if (drawer && target && source) {
          target.innerHTML = source.innerHTML;
          drawer.classList.toggle('is-empty', this.cart?.item_count === 0);
          drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
        }
      }

      if (sections['cart-icon-bubble']) {
        const target = document.getElementById('cart-icon-bubble');
        const html = LuxuryCartUtils.getSectionInnerHTML(sections['cart-icon-bubble']);
        if (target && html) target.innerHTML = html;
      }
    }

    _wireShopifyPubSub() {
      if (this._pubsubWired || typeof window.subscribe !== 'function' || !window.PUB_SUB_EVENTS?.cartUpdate) return;
      this._pubsubWired = true;

      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === 'cart-items') return;
        const cartData = event?.cartData;
        if (cartData && typeof cartData.items_subtotal_price === 'number') {
          this.receiveCart(cartData, { source: event.source || 'shopify-pubsub' });
          this.scheduleSectionRefresh(120);
        } else {
          this.scheduleRefresh(80, { source: event?.source || 'shopify-pubsub', renderSections: true });
        }
      });
    }

    _wireAjaxCartObserver() {
      if (window.__LuxuryCartFetchObserverWired) return;
      window.__LuxuryCartFetchObserverWired = true;

      window.fetch = (...args) => {
        const responsePromise = this._nativeFetch(...args);
        const request = this._normalizeFetchRequest(args[0], args[1]);

        if (!request.isCartRequest || this._managedFetchDepth > 0) return responsePromise;

        return responsePromise.then((response) => {
          this._handleObservedCartResponse(response, request);
          return response;
        });
      };
    }

    _normalizeFetchRequest(resource, init = {}) {
      const rawUrl = typeof resource === 'string' ? resource : resource?.url || '';
      const method = (init?.method || resource?.method || 'GET').toUpperCase();
      let pathname = '';

      try {
        pathname = new URL(rawUrl, window.location.origin).pathname.replace(/\/+$/, '');
      } catch (error) {
        return { isCartRequest: false, method, pathname: '' };
      }

      const cartJsonPath = this._pathWithOptionalJs(window.routes?.cart_url || CART_URL).find((path) =>
        path.endsWith('/cart.js')
      );
      const mutationPaths = [
        CART_ADD_URL,
        CART_CHANGE_URL,
        CART_UPDATE_URL,
        CART_CLEAR_URL,
        window.routes?.cart_add_url,
        window.routes?.cart_change_url,
        window.routes?.cart_update_url,
      ].flatMap((url) => this._pathWithOptionalJs(url));

      return {
        isCartRequest: pathname === cartJsonPath || mutationPaths.includes(pathname),
        isCartJson: pathname === cartJsonPath,
        isMutation: method !== 'GET' && mutationPaths.includes(pathname),
        method,
        pathname,
      };
    }

    _pathWithOptionalJs(rawUrl) {
      if (!rawUrl) return [];

      try {
        const path = new URL(rawUrl, window.location.origin).pathname.replace(/\/+$/, '');
        const altPath = path.endsWith('.js') ? path.replace(/\.js$/, '') : `${path}.js`;
        return LuxuryCartUtils.unique([path, altPath]);
      } catch (error) {
        return [];
      }
    }

    _handleObservedCartResponse(response, request) {
      if (!response?.ok) return;

      response
        .clone()
        .json()
        .then((payload) => {
          const fullCart = payload && typeof payload.items_subtotal_price === 'number' && Array.isArray(payload.items);

          if (fullCart) {
            this.receiveCart(payload, { source: request.isCartJson ? 'ajax-cart-json' : 'ajax-cart-mutation' });
            if (request.isMutation) this.scheduleSectionRefresh(120);
            return;
          }

          if (request.isMutation) {
            this.scheduleRefresh(100, { source: 'ajax-cart-mutation', renderSections: true });
          }
        })
        .catch(() => {
          if (request.isMutation) {
            this.scheduleRefresh(140, { source: 'ajax-cart-mutation', renderSections: true });
          }
        });
    }
  }

  class RewardManagerClass {
    constructor(cartManager) {
      this.cartManager = cartManager;
      this.bus = new EventBus();
      this.tiers = [
        { threshold: 2000_00, reward: 100_00 },
        { threshold: 3000_00, reward: 200_00 },
        { threshold: 4000_00, reward: 300_00 },
        { threshold: 5000_00, reward: 400_00 },
      ];

      this.cartManager.on('cart:updated', (cart) => {
        this.bus.emit('reward:updated', this.calculate(cart.items_subtotal_price));
      });
    }

    on(event, handler) {
      return this.bus.on(event, handler);
    }

    formatMoney(cents) {
      return LuxuryCartUtils.formatMoney(cents);
    }

    calculate(subtotalCents = 0) {
      const subtotal = Number(subtotalCents) || 0;
      const maxTier = this.tiers[this.tiers.length - 1];
      const unlockedTiers = this.tiers.filter((tier) => subtotal >= tier.threshold);
      const nextTier = this.tiers.find((tier) => subtotal < tier.threshold);
      const isMaxed = subtotal >= maxTier.threshold;
      const previousThreshold = unlockedTiers.length ? unlockedTiers[unlockedTiers.length - 1].threshold : 0;
      const progressPercent = isMaxed ? 100 : ProgressEngine.between(subtotal, previousThreshold, nextTier.threshold);
      const remainingToNext = isMaxed ? 0 : nextTier.threshold - subtotal;
      const lastUnlocked = unlockedTiers[unlockedTiers.length - 1] || null;

      let headline = 'Collector Rewards';
      let unlockedText = 'Private rewards await.';
      let message = `Only ${this.formatMoney(remainingToNext)} away from your next Collector Reward.`;

      if (isMaxed) {
        headline = "You're an Elite Collector.";
        unlockedText = 'All Collector Rewards unlocked.';
        message = 'All Collector Rewards unlocked.';
      } else if (lastUnlocked) {
        headline = 'Congratulations!';
        unlockedText = `${this.formatMoney(lastUnlocked.reward)} unlocked.`;
        message = `Spend only ${this.formatMoney(remainingToNext)} more to unlock ${this.formatMoney(
          nextTier.reward
        )} Reward.`;
      }

      return {
        subtotalCents: subtotal,
        tiers: this.tiers.map((tier) => ({
          ...tier,
          unlocked: subtotal >= tier.threshold,
          isCurrent: nextTier ? tier.threshold === nextTier.threshold : false,
          status: subtotal >= tier.threshold ? 'completed' : nextTier?.threshold === tier.threshold ? 'current' : 'future',
        })),
        unlockedTiers,
        nextTier,
        isMaxed,
        progressPercent,
        remainingToNext,
        headline,
        unlockedText,
        message,
        totalUnlockedReward: unlockedTiers.reduce((sum, tier) => sum + tier.reward, 0),
      };
    }

    async getCurrentState() {
      const cart = await this.cartManager.getCart();
      return this.calculate(cart.items_subtotal_price);
    }
  }

  class ShippingManagerClass {
    constructor(cartManager) {
      this.cartManager = cartManager;
      this.bus = new EventBus();
      this.threshold = 1199_00;

      this.cartManager.on('cart:updated', (cart) => {
        this.bus.emit('shipping:updated', this.calculate(cart.items_subtotal_price));
      });
    }

    on(event, handler) {
      return this.bus.on(event, handler);
    }

    calculate(subtotalCents = 0) {
      const subtotal = Number(subtotalCents) || 0;
      const remaining = Math.max(this.threshold - subtotal, 0);
      const unlocked = subtotal >= this.threshold;

      return {
        threshold: this.threshold,
        subtotalCents: subtotal,
        remaining,
        unlocked,
        progressPercent: unlocked ? 100 : ProgressEngine.toward(subtotal, this.threshold),
        message: unlocked
          ? 'Free Shipping unlocked!'
          : `Only ${LuxuryCartUtils.formatMoney(remaining)} away from FREE Shipping`,
      };
    }
  }

  const CartManager = new CartManagerClass();
  const RewardManager = new RewardManagerClass(CartManager);
  const ShippingManager = new ShippingManagerClass(CartManager);

  window.LuxuryCartUtils = LuxuryCartUtils;
  window.LuxuryAnimations = LuxuryAnimations;
  window.ProgressEngine = ProgressEngine;
  window.CartManager = CartManager;
  window.RewardManager = RewardManager;
  window.ShippingManager = ShippingManager;
})();
